<template>
  <!--
    Import delle motivazioni degli storni dal PDF degli insoluti della banca.
    Le finestre si aprono dai pulsanti in testata di RidSddBanche.vue, che
    chiama apriCaricamento() e apriStorico().
  -->
  <div class="rb-insoluti">

    <!-- Import: scelta del file, anteprima, esito -->
    <Dialog v-model:visible="mostraImport" :modal="true" :style="{ width: '62rem' }"
      :breakpoints="{ '900px': '94vw' }" :closable="!occupato" :closeOnEscape="!occupato" class="rb-dialog"
      @hide="onChiusuraImport">
      <template #header>
        <div class="rb-vh">
          <span class="rb-vh-titolo">{{ titoloImport }}</span>
          <span v-if="importCorrente" class="rb-vh-file">{{ importCorrente.nome_file }}</span>
        </div>
      </template>

      <div v-if="fase === 'attesa'" class="rb-caricamento">
        <i class="pi pi-spin pi-spinner"></i>
      </div>

      <!-- 1. Scelta del file -->
      <div v-else-if="fase === 'scelta'" class="rb-ins-scelta">
        <input id="rb-ins-file" type="file" accept=".pdf,application/pdf" class="rb-ins-input"
          :disabled="caricando" @change="onFileScelto" />
        <label for="rb-ins-file" class="rb-ins-zona"
          :class="{ 'rb-ins-zona--scelto': fileScelto, 'rb-ins-zona--occupata': caricando }">
          <i class="pi pi-file-pdf"></i>
          <span class="rb-ins-zona-nome">{{ fileScelto ? fileScelto.name : 'Scegli il PDF degli insoluti' }}</span>
          <span class="rb-ins-zona-meta">
            {{ fileScelto ? formatDimensione(fileScelto.size) + ' · clic per cambiarlo' : 'Solo file .pdf, come li produce la banca' }}
          </span>
        </label>

        <p class="rb-ins-nota">
          Il PDF viene archiviato e confrontato con gli storni registrati: le motivazioni si scrivono solo
          dopo la conferma.
        </p>

        <div v-if="erroreCaricamento" class="rb-esito rb-esito--ko">
          <i class="pi pi-exclamation-triangle"></i>
          <div>
            <div class="rb-esito-titolo">Caricamento non riuscito</div>
            <div class="rb-esito-testo">{{ erroreCaricamento }}</div>
          </div>
        </div>
      </div>

      <!-- 2. Anteprima e 3. Esito: stessa tabella -->
      <div v-else-if="riepilogo">
        <div v-for="(avviso, i) in avvisi" :key="i" class="rb-esito" :class="'rb-esito--' + avviso.tipo">
          <i :class="iconaAvviso(avviso.tipo)"></i>
          <div>
            <div class="rb-esito-titolo">{{ avviso.titolo }}</div>
            <div class="rb-esito-testo">{{ avviso.testo }}</div>
          </div>
        </div>

        <div class="rb-confronto">
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Banca</span>
            <span class="rb-banca" :class="'rb-banca--' + String(importCorrente.banca).toLowerCase()">
              {{ importCorrente.banca }}
            </span>
          </div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Righe nel PDF</span>
            <span class="rb-conf-num">{{ importCorrente.righe_totali }}</span>
          </div>
          <div class="rb-conf-sep"></div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Caricato</span>
            <span class="rb-conf-testo">{{ formatDateTime(importCorrente.created_at) }}</span>
            <span v-if="importCorrente.operatore_email" class="rb-sotto">{{ importCorrente.operatore_email }}</span>
          </div>
          <template v-if="fase === 'esito'">
            <div class="rb-conf-voce">
              <span class="rb-conf-lab">Confermato</span>
              <span class="rb-conf-testo">{{ formatDateTime(importCorrente.confermato_at) }}</span>
              <span v-if="importCorrente.confermato_da_email" class="rb-sotto">
                {{ importCorrente.confermato_da_email }}
              </span>
            </div>
            <div class="rb-conf-voce">
              <span class="rb-conf-lab">Storni aggiornati</span>
              <span class="rb-conf-num rb-conf-ok">{{ importCorrente.aggiornati }}</span>
            </div>
          </template>
        </div>

        <div v-if="conteggiVisibili.length" class="rb-ins-conteggi">
          <span v-for="c in conteggiVisibili" :key="c.esito" class="rb-stato" :class="'rb-stato--' + c.esito">
            <span class="rb-stato-pallino"></span>
            {{ c.etichetta }}
            <span class="rb-ins-conteggio-num">{{ c.numero }}</span>
          </span>
        </div>

        <div class="rb-verifica-barra">
          <span class="rb-verifica-conteggio">
            {{ righeFiltrate.length }} di {{ righeImport.length }} righe mostrate
          </span>
          <SelectButton v-model="filtro" :options="opzioniFiltro" optionLabel="label" optionValue="value"
            :allowEmpty="false" class="rb-ins-filtro" @change="primaRiga = 0" />
        </div>

        <DataTable :value="righeFiltrate" :paginator="righeFiltrate.length > righePerPagina" :rows="righePerPagina"
          v-model:first="primaRiga" dataKey="id" responsiveLayout="scroll" stripedRows
          class="p-datatable-sm rb-dt rb-dt-verifica">
          <template #empty>
            <div class="rb-vuoto"><p class="rb-vuoto-testo">Nessuna riga da mostrare</p></div>
          </template>

          <Column field="riga" header="Riga" style="width: 4rem">
            <template #body="{ data }">
              <span class="rb-mono">{{ data.riga }}</span>
            </template>
          </Column>

          <Column field="data_scadenza" header="Scadenza" style="width: 6.5rem">
            <template #body="{ data }">
              <span v-if="data.data_scadenza" class="rb-incasso">{{ formatDate(data.data_scadenza) }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>

          <Column field="importo" header="Importo" style="width: 7rem">
            <template #body="{ data }">
              <span v-if="data.importo !== null" class="rb-importo">{{ formatCurrency(data.importo) }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>

          <Column field="debitore_nome" header="Debitore" style="min-width: 11rem">
            <template #body="{ data }">
              <div class="rb-cella-multi">
                <span v-if="data.debitore_nome">{{ data.debitore_nome }}</span>
                <span v-else class="rb-vuoto-cella">—</span>
                <span v-if="data.debitore_iban" class="rb-sotto rb-mono">{{ data.debitore_iban }}</span>
              </div>
            </template>
          </Column>

          <Column header="Causale" style="min-width: 12rem">
            <template #body="{ data }">
              <div class="rb-ins-causale">
                <span v-if="data.causale_codice" class="rb-ins-codice">{{ data.causale_codice }}</span>
                <span v-if="data.causale_testo">{{ data.causale_testo }}</span>
                <span v-if="!data.causale_codice && !data.causale_testo" class="rb-vuoto-cella">—</span>
              </div>
            </template>
          </Column>

          <Column field="esito" header="Esito" style="width: 9.5rem">
            <template #body="{ data }">
              <span class="rb-stato" :class="'rb-stato--' + data.esito">
                <span class="rb-stato-pallino"></span>
                {{ etichettaEsito(data.esito) }}
              </span>
            </template>
          </Column>

          <Column field="fattura" header="Fattura" style="width: 7rem">
            <template #body="{ data }">
              <span v-if="data.fattura" class="rb-mono">{{ data.fattura }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>

          <!--
            Per le righe che si aggiornano il passaggio del cursore mostra la
            motivazione: proposta nell'anteprima, scritta nell'esito.
          -->
          <Column header="Motivo o candidati" style="min-width: 13rem">
            <template #body="{ data }">
              <span class="rb-ins-motivo" :class="{ 'rb-ins-motivo--motivazione': data.motivazione }"
                v-tooltip.top="data.motivazione ? 'Motivazione: ' + data.motivazione : null">{{ motivo(data) }}</span>
              <div v-if="data.candidati && data.candidati.length" class="rb-ins-candidati">
                <span v-for="c in data.candidati" :key="c.payment_id" class="rb-ins-candidato rb-mono"
                  v-tooltip.top="'Storno n. ' + c.payment_id">{{ c.fattura || 'n. ' + c.payment_id }}</span>
              </div>
            </template>
          </Column>
        </DataTable>

        <p v-if="fase === 'anteprima'" class="rb-ins-nota">
          Chiudendo, l'anteprima resta nello storico come non confermata e si può confermare in seguito.
        </p>
      </div>

      <template #footer>
        <template v-if="fase === 'scelta'">
          <Button label="Annulla" icon="pi pi-times" class="p-button-text rb-btn" :disabled="caricando"
            @click="mostraImport = false" />
          <Button label="Carica" icon="pi pi-upload" class="rb-btn" :loading="caricando" :disabled="!fileScelto"
            @click="carica" />
        </template>

        <template v-else-if="fase === 'anteprima'">
          <Button label="Chiudi" icon="pi pi-times" class="p-button-text rb-btn" :disabled="occupato"
            @click="mostraImport = false" />
          <Button v-if="statoIncerto" label="Rileggi" icon="pi pi-refresh" class="rb-btn" :loading="rileggendo"
            @click="rileggiImport(importCorrente.id)" />
          <Button v-else :label="etichettaConferma" icon="pi pi-check" class="rb-btn" :loading="confermando"
            :disabled="daAggiornare === 0 || occupato" @click="chiediConferma" />
        </template>

        <template v-else-if="fase === 'esito'">
          <Button label="Scarica PDF" icon="pi pi-download" class="p-button-outlined p-button-secondary rb-btn"
            :loading="scaricando === importCorrente.id" @click="scaricaPdf(importCorrente)" />
          <Button label="Chiudi" icon="pi pi-times" class="p-button-text rb-btn" :disabled="occupato"
            @click="mostraImport = false" />
          <Button v-if="daAggiornare > 0" label="Completa l'aggiornamento" icon="pi pi-check" class="rb-btn"
            :loading="confermando" :disabled="occupato" @click="chiediConferma" />
        </template>
      </template>
    </Dialog>

    <!-- Conferma dell'aggiornamento: irreversibile, quindi con l'attesa -->
    <Dialog v-model:visible="mostraConferma" header="Scrivere le motivazioni degli storni?" :modal="true"
      :style="{ width: '30rem' }" :breakpoints="{ '900px': '94vw' }" :closable="false" class="rb-dialog">
      <div class="rb-conferma">
        <div class="rb-conferma-icona rb-conferma-icona--conferma">
          <i class="pi pi-check-circle"></i>
        </div>
        <div class="rb-conferma-testo">
          <p class="rb-conferma-msg">{{ messaggioConferma }}</p>
          <div v-if="importCorrente" class="rb-conferma-file">
            <div class="rb-conferma-file-nome">{{ importCorrente.nome_file }}</div>
            <div class="rb-conferma-file-meta">
              {{ importCorrente.banca }} · {{ importCorrente.righe_totali }} righe · {{ daAggiornare }} da aggiornare
            </div>
          </div>
          <div v-if="attesaConferma" class="rb-attesa">
            <span>Conferma disponibile tra {{ countdown }}s</span>
            <div class="rb-attesa-barra">
              <div class="rb-attesa-avanzamento" :style="{ width: progressoConferma + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text rb-btn" @click="chiudiConferma" />
        <Button label="Conferma" icon="pi pi-check" class="rb-btn" :disabled="attesaConferma || confermando"
          @click="confermaImport" />
      </template>
    </Dialog>

    <!-- Storico: gli import per file e le righe ignorate di tutti gli import -->
    <!-- Larga quanto serve alle colonne degli ignorati, dove lo schermo lo permette -->
    <Dialog v-model:visible="mostraStorico" :modal="true" :style="{ width: '96rem', maxWidth: '94vw' }"
      :breakpoints="{ '900px': '94vw' }" class="rb-dialog">
      <template #header>
        <div class="rb-vh">
          <span class="rb-vh-titolo">Storico insoluti</span>
          <span class="rb-vh-sotto">{{ sottotitoloStorico }}</span>
        </div>
      </template>

      <!-- Ogni scheda si rilegge quando viene mostrata (onSchedaStorico) -->
      <TabView v-model:activeIndex="schedaStorico" class="rb-ins-schede" @tab-change="onSchedaStorico">
        <TabPanel header="Per file">
          <DataTable :value="storico" :lazy="true" :paginator="totaleStorico > perPaginaStorico"
            :rows="perPaginaStorico" :first="(paginaStorico - 1) * perPaginaStorico" :totalRecords="totaleStorico"
            :loading="caricandoStorico" dataKey="id" responsiveLayout="scroll" @page="onPaginaStorico"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}" class="p-datatable-sm rb-dt">
            <template #empty>
              <div class="rb-vuoto">
                <i class="pi pi-inbox"></i>
                <p class="rb-vuoto-titolo">Nessun import</p>
                <p class="rb-vuoto-testo">I PDF degli insoluti caricati compaiono qui, anche quelli non confermati.</p>
              </div>
            </template>

            <Column field="created_at" header="Data" style="width: 8.5rem">
              <template #body="{ data }">
                <span class="rb-incasso">{{ formatDateTime(data.created_at) }}</span>
              </template>
            </Column>

            <Column field="operatore_email" header="Operatore" style="min-width: 11rem">
              <template #body="{ data }">
                <div class="rb-cella-multi">
                  <span v-if="data.operatore_email" class="rb-ins-email">{{ data.operatore_email }}</span>
                  <span v-else class="rb-vuoto-cella">—</span>
                  <span v-if="data.stato === 'CONFERMATO' && data.confermato_da_email" class="rb-sotto">
                    confermato da {{ data.confermato_da_email }}
                  </span>
                </div>
              </template>
            </Column>

            <Column field="nome_file" header="File" style="min-width: 13rem">
              <template #body="{ data }">
                <div class="rb-file">
                  <i class="pi pi-file-pdf rb-file-icona"></i>
                  <span class="rb-file-nome">{{ data.nome_file }}</span>
                </div>
              </template>
            </Column>

            <Column field="banca" header="Banca" style="width: 6rem">
              <template #body="{ data }">
                <span class="rb-banca" :class="'rb-banca--' + String(data.banca).toLowerCase()">{{ data.banca }}</span>
              </template>
            </Column>

            <Column field="stato" header="Stato" style="width: 9.5rem">
              <template #body="{ data }">
                <span class="rb-stato" :class="'rb-stato--' + String(data.stato).toLowerCase()">
                  <span class="rb-stato-pallino"></span>
                  {{ etichettaStatoImport(data.stato) }}
                </span>
              </template>
            </Column>

            <Column field="righe_totali" header="Righe" style="width: 5rem">
              <template #body="{ data }">
                <span class="rb-importo">{{ data.righe_totali }}</span>
              </template>
            </Column>

            <Column field="aggiornati" header="Aggiornati" style="width: 6.5rem">
              <template #body="{ data }">
                <span class="rb-importo">{{ data.aggiornati }}</span>
              </template>
            </Column>

            <Column header="Azioni" style="width: 6rem" bodyClass="rb-cella-azioni">
              <template #body="{ data }">
                <div class="rb-azioni">
                  <Button icon="pi pi-eye" class="rb-azione" v-tooltip.top="tooltipApri(data)"
                    @click="apriImport(data)" />
                  <Button icon="pi pi-download" class="rb-azione rb-azione--ok" v-tooltip.top="'Scarica PDF'"
                    :loading="scaricando === data.id" @click="scaricaPdf(data)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </TabPanel>

        <!--
          Le righe dei PDF che l'import non ha applicato, di tutti gli import.
          La barra filtra lato server per esito, import, scadenza e testo, e
          ogni cambio riparte da pagina 1. I badge sono solo un riepilogo: non
          si cliccano e contano le righe dentro import, date e testo, senza
          seguire gli esiti scelti.
        -->
        <TabPanel header="Ignorati">
          <div class="rb-ins-filtri" role="search" aria-label="Filtri delle righe ignorate">
            <div class="rb-ins-f">
              <span id="rb-ins-f-esiti" class="rb-ins-f-lab">Esito</span>
              <MultiSelect :modelValue="filtriIgnorati.esiti" :options="opzioniEsitiIgnorati" optionLabel="etichetta"
                optionValue="esito" placeholder="Tutti gli esiti" :maxSelectedLabels="1"
                selectedItemsLabel="{0} esiti selezionati" :showToggleAll="false" ariaLabelledby="rb-ins-f-esiti"
                class="rb-ins-f-campo" @update:modelValue="onEsitiIgnorati" />
            </div>

            <div class="rb-ins-f rb-ins-f--import">
              <span id="rb-ins-f-import" class="rb-ins-f-lab">Import</span>
              <Dropdown :modelValue="filtriIgnorati.importId" :options="opzioniImportIgnorati" optionLabel="etichetta"
                optionValue="id" placeholder="Tutti gli import" :filter="true" filterPlaceholder="Cerca un file"
                :showClear="true" :loading="caricandoImport" ariaLabelledby="rb-ins-f-import" class="rb-ins-f-campo"
                @update:modelValue="impostaFiltroIgnorati('importId', $event)" />
            </div>

            <div class="rb-ins-f rb-ins-f--scadenza" role="group" aria-labelledby="rb-ins-f-scadenza">
              <span id="rb-ins-f-scadenza" class="rb-ins-f-lab">Scadenza dal / al</span>
              <div class="rb-ins-periodo">
                <Calendar :modelValue="filtriIgnorati.dal" dateFormat="dd/mm/yy" placeholder="gg/mm/aaaa"
                  :showButtonBar="true" ariaLabel="Scadenza dal" class="rb-ins-periodo-data"
                  @update:modelValue="impostaFiltroIgnorati('dal', $event)" />
                <span class="rb-ins-periodo-sep" aria-hidden="true">—</span>
                <Calendar :modelValue="filtriIgnorati.al" dateFormat="dd/mm/yy" placeholder="gg/mm/aaaa"
                  :showButtonBar="true" ariaLabel="Scadenza al" class="rb-ins-periodo-data"
                  @update:modelValue="impostaFiltroIgnorati('al', $event)" />
              </div>
            </div>

            <div class="rb-ins-f rb-ins-f--cerca">
              <label for="rb-ins-f-cerca" class="rb-ins-f-lab">Cerca</label>
              <span class="p-input-icon-left rb-ins-f-cerca-box">
                <i class="pi pi-search"></i>
                <InputText id="rb-ins-f-cerca" :modelValue="filtriIgnorati.cerca" placeholder="Debitore o IBAN"
                  autocomplete="off" class="rb-ins-f-campo" @update:modelValue="onCercaIgnorati"
                  @keyup.enter="ripartiIgnorati" />
              </span>
            </div>

            <Button label="Azzera filtri" icon="pi pi-filter-slash"
              class="p-button-outlined p-button-secondary rb-btn rb-ins-azzera" :disabled="!filtriIgnoratiAttivi"
              @click="azzeraFiltriIgnorati" />
          </div>

          <p v-if="filtriIgnorati.dal || filtriIgnorati.al" class="rb-ins-nota-filtri">
            <i class="pi pi-info-circle" aria-hidden="true"></i>
            Le righe senza scadenza, per esempio alcune illeggibili, non compaiono quando c'è una data.
          </p>

          <div v-if="conteggiIgnorati" class="rb-ins-riepilogo">
            <span class="rb-verifica-conteggio">
              <span class="rb-ins-conteggio-num">{{ plurale(totaleConteggiIgnorati, 'riga ignorata', 'righe ignorate') }}</span>
              <template v-if="filtriIgnorati.esiti.length"> · {{ totaleIgnorati }} con gli esiti scelti</template>
            </span>
            <span v-for="c in conteggiVisibiliIgnorati" :key="c.esito" class="rb-stato" :class="'rb-stato--' + c.esito">
              <span class="rb-stato-pallino"></span>
              {{ c.etichetta }}
              <span class="rb-ins-conteggio-num">{{ c.numero }}</span>
            </span>
          </div>

          <DataTable :value="ignorati" :lazy="true" :paginator="totaleIgnorati > perPaginaIgnorati"
            :rows="perPaginaIgnorati" :first="(paginaIgnorati - 1) * perPaginaIgnorati" :totalRecords="totaleIgnorati"
            :loading="caricandoIgnorati" dataKey="id" responsiveLayout="scroll" stripedRows @page="onPaginaIgnorati"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}"
            class="p-datatable-sm rb-dt rb-dt-verifica">
            <template #empty>
              <div class="rb-vuoto">
                <i class="pi pi-inbox"></i>
                <p class="rb-vuoto-titolo">{{ vuotoIgnorati.titolo }}</p>
                <p class="rb-vuoto-testo">{{ vuotoIgnorati.testo }}</p>
              </div>
            </template>

            <Column header="Import" style="min-width: 11rem">
              <template #body="{ data }">
                <div class="rb-file">
                  <i class="pi pi-file-pdf rb-file-icona"></i>
                  <div class="rb-cella-multi">
                    <span class="rb-file-nome">{{ data.import.nome_file }}</span>
                    <span class="rb-sotto">{{ quandoImport(data.import) }}</span>
                  </div>
                </div>
              </template>
            </Column>

            <Column field="riga" header="Riga" style="width: 4rem">
              <template #body="{ data }">
                <span class="rb-mono">{{ data.riga }}</span>
              </template>
            </Column>

            <Column field="data_scadenza" header="Scadenza" style="width: 6.5rem">
              <template #body="{ data }">
                <span v-if="data.data_scadenza" class="rb-incasso">{{ formatDate(data.data_scadenza) }}</span>
                <span v-else class="rb-vuoto-cella">—</span>
              </template>
            </Column>

            <Column field="importo" header="Importo" style="width: 7rem">
              <template #body="{ data }">
                <span v-if="data.importo !== null" class="rb-importo">{{ formatCurrency(data.importo) }}</span>
                <span v-else class="rb-vuoto-cella">—</span>
              </template>
            </Column>

            <Column field="debitore_nome" header="Debitore" style="min-width: 11rem">
              <template #body="{ data }">
                <div class="rb-cella-multi">
                  <span v-if="data.debitore_nome">{{ data.debitore_nome }}</span>
                  <span v-else class="rb-vuoto-cella">—</span>
                  <span v-if="data.debitore_iban" class="rb-sotto rb-mono">{{ data.debitore_iban }}</span>
                </div>
              </template>
            </Column>

            <Column header="Causale" style="min-width: 12rem">
              <template #body="{ data }">
                <div class="rb-ins-causale">
                  <span v-if="data.causale_codice" class="rb-ins-codice">{{ data.causale_codice }}</span>
                  <span v-if="data.causale_testo">{{ data.causale_testo }}</span>
                  <span v-if="!data.causale_codice && !data.causale_testo" class="rb-vuoto-cella">—</span>
                </div>
              </template>
            </Column>

            <Column field="esito" header="Esito" style="width: 9.5rem">
              <template #body="{ data }">
                <span class="rb-stato" :class="'rb-stato--' + data.esito">
                  <span class="rb-stato-pallino"></span>
                  {{ etichettaEsito(data.esito) }}
                </span>
              </template>
            </Column>

            <!--
              Qui i candidati sono solo id (niente fattura): per gli ambigui i
              possibili storni, per un IBAN diverso lo storno trovato.
            -->
            <Column header="Motivo o candidati" style="min-width: 13rem">
              <template #body="{ data }">
                <span class="rb-ins-motivo">{{ motivo(data) }}</span>
                <div v-if="storniCitati(data).length" class="rb-ins-candidati">
                  <span v-for="id in storniCitati(data)" :key="id" class="rb-ins-candidato rb-mono"
                    v-tooltip.top="'Storno n. ' + id">n. {{ id }}</span>
                </div>
              </template>
            </Column>

            <Column header="Azioni" style="width: 5rem" bodyClass="rb-cella-azioni">
              <template #body="{ data }">
                <div class="rb-azioni">
                  <Button icon="pi pi-eye" class="rb-azione" aria-label="Apri import" v-tooltip.top="'Apri import'"
                    @click="apriImport(data.import)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </TabPanel>
      </TabView>

      <template #footer>
        <Button label="Aggiorna" icon="pi pi-refresh" class="p-button-outlined p-button-secondary rb-btn"
          :loading="caricandoSchedaStorico" @click="caricaSchedaStorico()" />
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text rb-btn" @click="mostraStorico = false" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount, getCurrentInstance } from "vue";
import moment from "moment";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import SelectButton from "primevue/selectbutton";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import MultiSelect from "primevue/multiselect";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import InputText from "primevue/inputtext";

import RidSddBancheService from "@/services/ask-pratiche/rid-sdd-banche-service";

/** Esiti di riga (specifica §2.1 e §2.3), nell'ordine dei conteggi del server. */
const ETICHETTE_ESITO = {
  aggiornabile: "Da aggiornare",
  ambiguo: "Ambiguo",
  iban_diverso: "IBAN diverso",
  gia_motivato: "Già motivato",
  non_trovato: "Non trovato",
  conto_sconosciuto: "Conto sconosciuto",
  illeggibile: "Illeggibile",
  aggiornato: "Aggiornato",
  gia_motivato_nel_frattempo: "Cambiato dopo l'anteprima",
};

/** Perché la riga ha quell'esito, detto all'operatore. */
const MOTIVI_ESITO = {
  aggiornabile: "Storno libero: riceverà la motivazione",
  aggiornato: "Motivazione scritta nello storno",
  iban_diverso: "Il mandato della fattura ha un IBAN diverso da quello del PDF",
  gia_motivato: "Lo storno ha già una motivazione",
  gia_motivato_nel_frattempo: "Motivato o modificato da altri dopo l'anteprima",
  non_trovato: "Nessuno storno con questa scadenza, importo e conto, o cancellato prima della conferma",
  conto_sconosciuto: "Conto di accredito non riconosciuto",
  illeggibile: "Mancano dati nel PDF: scadenza, importo, conto o causale",
};

/** Righe che l'operatore deve guardare: nessuna riceve la motivazione. */
const ESITI_DA_CONTROLLARE = ["ambiguo", "iban_diverso", "non_trovato", "conto_sconosciuto", "illeggibile"];

const ETICHETTE_STATO_IMPORT = {
  ANTEPRIMA: "Non confermato",
  CONFERMATO: "Confermato",
};

const TITOLI_FASE = {
  attesa: "Import degli insoluti",
  scelta: "Carica il PDF degli insoluti",
  anteprima: "Anteprima dell'import",
  esito: "Esito dell'import",
};

/** Come le azioni irreversibili della pagina: qualche secondo per rileggere. */
const ATTESA_SECONDI = 5;

const RIGHE_PER_PAGINA = 12;
const STORICO_PER_PAGINA = 10;
const IGNORATI_PER_PAGINA = 10;

/** Quanti import si leggono per il filtro "Import": il massimo che il backend dà in una pagina. */
const IMPORT_PER_FILTRO = 200;

/** Pausa dopo l'ultimo carattere digitato prima di rileggere gli ignorati. */
const ATTESA_RICERCA_MS = 400;

/** Filtri della scheda Ignorati: tutto vuoto vale "nessun filtro". */
const nuoviFiltriIgnorati = () => ({ esiti: [], importId: null, dal: null, al: null, cerca: "" });

/** Schede della finestra dello storico, nell'ordine del TabView. */
const SCHEDA_PER_FILE = 0;
const SCHEDA_IGNORATI = 1;

const SOTTOTITOLI_STORICO = {
  [SCHEDA_PER_FILE]: "PDF degli insoluti caricati, dal più recente",
  [SCHEDA_IGNORATI]: "Righe dei PDF che l'import non ha applicato, dall'import più recente",
};

export default {
  name: "RidSddBancheInsoluti",
  components: {
    DataTable, Column, Button, Dialog, SelectButton, TabView, TabPanel, MultiSelect, Dropdown, Calendar, InputText,
  },

  setup() {
    const { proxy } = getCurrentInstance();
    const service = new RidSddBancheService(proxy.$http, proxy.$eventBus);

    // ── Finestra dell'import ──────────────────────────────────────────

    const mostraImport = ref(false);
    const fileScelto = ref(null);
    const erroreCaricamento = ref(null);
    const caricando = ref(false);
    const caricandoRiepilogo = ref(false);
    const confermando = ref(false);
    const rileggendo = ref(false);
    const riepilogo = ref(null);
    const filtro = ref("tutte");
    const primaRiga = ref(0);

    // Dopo una conferma non riuscita: l'avviso da mostrare, se l'errore era
    // un'interruzione (non un 409) e se lo stato in pagina è quello vero.
    const avvisoConferma = ref(null);
    const confermaInterrotta = ref(false);
    const statoIncerto = ref(false);

    const occupato = computed(() => caricando.value || confermando.value || rileggendo.value);

    /**
     * La fase discende dallo stato dell'import, non da un passaggio di
     * schermata: un import riletto dopo un errore si mostra per quello che è.
     */
    const fase = computed(() => {
      if (caricandoRiepilogo.value) return "attesa";
      if (!riepilogo.value) return "scelta";
      return riepilogo.value.import.stato === "CONFERMATO" ? "esito" : "anteprima";
    });

    const titoloImport = computed(() => TITOLI_FASE[fase.value]);
    const importCorrente = computed(() => (riepilogo.value ? riepilogo.value.import : null));
    const righeImport = computed(() => (riepilogo.value ? riepilogo.value.righe || [] : []));
    const daAggiornare = computed(() => (riepilogo.value ? riepilogo.value.conteggi.aggiornabile || 0 : 0));

    const conteggiVisibili = computed(() => {
      const c = riepilogo.value ? riepilogo.value.conteggi || {} : {};
      return Object.keys(c)
        .filter((esito) => c[esito] > 0)
        .map((esito) => ({ esito, etichetta: etichettaEsito(esito), numero: c[esito] }));
    });

    // ── Filtro delle righe ────────────────────────────────────────────

    /** Prima della conferma interessano le righe da scrivere, dopo quelle scritte. */
    const esitiDaAggiornare = computed(() => (fase.value === "esito" ? ["aggiornato"] : ["aggiornabile"]));

    const righePerFiltro = (f) => {
      if (f === "da_aggiornare") return righeImport.value.filter((r) => esitiDaAggiornare.value.includes(r.esito));
      if (f === "da_controllare") return righeImport.value.filter((r) => ESITI_DA_CONTROLLARE.includes(r.esito));
      return righeImport.value;
    };

    const righeFiltrate = computed(() => righePerFiltro(filtro.value));

    const opzioniFiltro = computed(() => [
      { label: `Tutte (${righeImport.value.length})`, value: "tutte" },
      {
        label: `${fase.value === "esito" ? "Aggiornate" : "Da aggiornare"} (${righePerFiltro("da_aggiornare").length})`,
        value: "da_aggiornare",
      },
      { label: `Da controllare (${righePerFiltro("da_controllare").length})`, value: "da_controllare" },
    ]);

    const mostraRiepilogo = (r) => {
      riepilogo.value = r;
      filtro.value = "tutte";
      primaRiga.value = 0;
    };

    // ── Avvisi ────────────────────────────────────────────────────────

    const avvisi = computed(() => {
      const r = riepilogo.value;
      if (!r) return [];

      const lista = [];
      if (avvisoConferma.value) lista.push(avvisoConferma.value);

      if (fase.value === "anteprima" && r.gia_importato_il) {
        lista.push({
          tipo: "warn",
          titolo: "File già importato",
          testo: `Lo stesso PDF è stato caricato il ${formatDateTime(r.gia_importato_il)}. `
            + "Gli storni già motivati non vengono toccati.",
        });
      }

      if (fase.value === "esito") lista.push(avvisoEsito(r));

      return lista;
    });

    const avvisoEsito = (r) => {
      const c = r.conteggi || {};

      // Un import confermato con righe ancora aggiornabili è una conferma
      // rimasta a metà: va detto, non mostrato come un successo.
      if (c.aggiornabile > 0) {
        return {
          tipo: "ko",
          titolo: "Aggiornamento incompleto",
          testo: `L'import risulta confermato, ma ${plurale(c.aggiornabile, "riga è", "righe sono")} ancora da `
            + "aggiornare: la conferma si è interrotta. «Completa l'aggiornamento» scrive le righe rimaste; "
            + "se la conferma è di pochi minuti fa potrebbe essere ancora in corso.",
        };
      }

      let testo = `${plurale(c.aggiornato || 0, "storno aggiornato", "storni aggiornati")}.`;
      if (c.gia_motivato_nel_frattempo) {
        testo += ` ${plurale(c.gia_motivato_nel_frattempo, "storno era stato motivato o modificato", "storni erano stati motivati o modificati")} `
          + "da altri dopo l'anteprima.";
      }

      return { tipo: "ok", titolo: "Import confermato", testo };
    };

    const avvisoInterruzione = (r) => {
      const scritti = r.conteggi ? r.conteggi.aggiornato || 0 : 0;
      let testo = "Lo stato mostrato è quello riletto dopo l'errore: "
        + `${plurale(scritti, "storno risulta aggiornato", "storni risultano aggiornati")}.`;
      if (r.import.stato === "ANTEPRIMA") {
        testo += " Una nuova conferma scrive solo le righe rimaste.";
      }
      return { tipo: "ko", titolo: "Conferma interrotta", testo };
    };

    const iconaAvviso = (tipo) => {
      if (tipo === "ok") return "pi pi-check-circle";
      if (tipo === "warn") return "pi pi-info-circle";
      return "pi pi-exclamation-triangle";
    };

    // ── Apertura ──────────────────────────────────────────────────────

    let richiestaRiepilogo = 0;

    const azzeraImport = () => {
      richiestaRiepilogo += 1; // un riepilogo ancora in viaggio non riempie la finestra azzerata
      caricandoRiepilogo.value = false;
      fileScelto.value = null;
      erroreCaricamento.value = null;
      riepilogo.value = null;
      filtro.value = "tutte";
      primaRiga.value = 0;
      avvisoConferma.value = null;
      confermaInterrotta.value = false;
      statoIncerto.value = false;
    };

    const apriCaricamento = () => {
      azzeraImport();
      mostraImport.value = true;
    };

    /** Dallo storico: anteprima se non confermato, altrimenti esito. */
    const apriImport = async (imp) => {
      azzeraImport();
      const richiesta = richiestaRiepilogo;
      mostraImport.value = true;
      caricandoRiepilogo.value = true;
      try {
        const r = await service.getImportInsoluti(imp.id);
        if (richiesta === richiestaRiepilogo) mostraRiepilogo(r);
      } catch (err) {
        if (richiesta !== richiestaRiepilogo) return;
        mostraImport.value = false;
        gestisciErrore(err, "Apertura non riuscita");
      } finally {
        if (richiesta === richiestaRiepilogo) caricandoRiepilogo.value = false;
      }
    };

    /**
     * Lo storico sotto la finestra deve mostrare i dati di adesso: stato e
     * aggiornati per file, e gli ignorati, che una conferma può aumentare
     * (gli storni motivati o modificati da altri nel frattempo).
     */
    const onChiusuraImport = () => {
      chiudiConferma();
      if (mostraStorico.value) caricaSchedaStorico();
    };

    // ── Caricamento ───────────────────────────────────────────────────

    const onFileScelto = (evt) => {
      const file = evt.target.files && evt.target.files[0];
      erroreCaricamento.value = null;

      if (!file) {
        fileScelto.value = null;
        return;
      }

      if (!/\.pdf$/i.test(file.name)) {
        fileScelto.value = null;
        erroreCaricamento.value = "Il file scelto non è un PDF.";
        return;
      }

      fileScelto.value = file;
    };

    const carica = async () => {
      if (!fileScelto.value || caricando.value) return;

      caricando.value = true;
      erroreCaricamento.value = null;
      try {
        mostraRiepilogo(await service.caricaInsoluti(fileScelto.value));
      } catch (err) {
        // Gli errori del caricamento restano nella finestra (§5): l'operatore
        // può scegliere un altro file senza riaprirla.
        erroreCaricamento.value = messaggioErrore(err);
      } finally {
        caricando.value = false;
      }
    };

    // ── Conferma ──────────────────────────────────────────────────────

    const mostraConferma = ref(false);
    const attesaConferma = ref(false);
    const countdown = ref(0);
    const progressoConferma = ref(0);
    let timerConferma = null;

    const etichettaConferma = computed(() => {
      const n = daAggiornare.value;
      if (!n) return "Nessun aggiornamento da confermare";
      return n === 1 ? "Conferma 1 aggiornamento" : `Conferma ${n} aggiornamenti`;
    });

    const messaggioConferma = computed(() => {
      const n = daAggiornare.value;
      return `${plurale(n, "storno riceverà", "storni riceveranno")} la motivazione letta dal PDF. `
        + "La modifica non si annulla dalla pagina.";
    });

    const chiediConferma = () => {
      if (!daAggiornare.value || occupato.value) return;
      mostraConferma.value = true;
      avviaAttesa();
    };

    const avviaAttesa = () => {
      attesaConferma.value = true;
      countdown.value = ATTESA_SECONDI;
      progressoConferma.value = 0;
      clearInterval(timerConferma);
      timerConferma = setInterval(() => {
        countdown.value -= 1;
        progressoConferma.value = ((ATTESA_SECONDI - countdown.value) / ATTESA_SECONDI) * 100;
        if (countdown.value <= 0) {
          clearInterval(timerConferma);
          attesaConferma.value = false;
        }
      }, 1000);
    };

    const chiudiConferma = () => {
      clearInterval(timerConferma);
      mostraConferma.value = false;
      attesaConferma.value = false;
    };

    const confermaImport = async () => {
      if (confermando.value) return;

      const imp = importCorrente.value;
      chiudiConferma();

      if (!imp) return;

      confermando.value = true;
      avvisoConferma.value = null;
      try {
        const r = await service.confermaInsoluti(imp.id);
        mostraRiepilogo(r);
        proxy.$toast.add({
          severity: "success",
          summary: "Import confermato",
          detail: `${imp.nome_file}: ${plurale(r.import.aggiornati, "storno aggiornato", "storni aggiornati")}`,
          life: 5000,
        });
      } catch (err) {
        gestisciErrore(err, "Conferma non riuscita");
        // Un 409 vuol dire che la conferma l'ha già fatta, o la sta facendo,
        // qualcun altro; ogni altro errore può averla interrotta a metà (§4.3).
        confermaInterrotta.value = statoHttp(err) !== 409;
        await rileggiImport(imp.id);
      } finally {
        confermando.value = false;
      }
    };

    /**
     * Dopo un errore della conferma la risposta non dice se l'import sia
     * tornato in anteprima né quante righe siano state scritte: si rilegge
     * invece di dedurlo. Se neanche la rilettura riesce, la conferma resta
     * sostituita da "Rileggi" finché lo stato non è noto.
     */
    const rileggiImport = async (id) => {
      rileggendo.value = true;
      try {
        const r = await service.getImportInsoluti(id);
        mostraRiepilogo(r);
        statoIncerto.value = false;
        avvisoConferma.value = confermaInterrotta.value ? avvisoInterruzione(r) : null;
      } catch (err) {
        statoIncerto.value = true;
        avvisoConferma.value = {
          tipo: "ko",
          titolo: "Stato dell'import non verificabile",
          testo: "La conferma non è riuscita e l'import non si è potuto rileggere: alcune righe potrebbero "
            + `essere già state scritte. Usa «Rileggi» prima di riprovare. (${messaggioErrore(err)})`,
        };
      } finally {
        rileggendo.value = false;
      }
    };

    // ── Download del PDF ──────────────────────────────────────────────

    const scaricando = ref(null);

    const scaricaPdf = async (imp) => {
      if (!imp || scaricando.value) return;

      scaricando.value = imp.id;
      try {
        await service.scaricaPdfInsoluti(imp.id, imp.nome_file);
      } catch (err) {
        gestisciErrore(err, "Download non riuscito");
      } finally {
        scaricando.value = null;
      }
    };

    // ── Storico ───────────────────────────────────────────────────────

    const mostraStorico = ref(false);
    const caricandoStorico = ref(false);
    const storico = ref([]);
    const totaleStorico = ref(0);
    const paginaStorico = ref(1);

    const caricaStorico = async () => {
      caricandoStorico.value = true;
      try {
        const r = await service.getStoricoInsoluti(paginaStorico.value, STORICO_PER_PAGINA);
        storico.value = r.items;
        totaleStorico.value = r.total;
      } catch (err) {
        gestisciErrore(err, "Storico non disponibile");
      } finally {
        caricandoStorico.value = false;
      }
    };

    /** Si apre sempre sulla scheda per file, con entrambe le schede azzerate. */
    const apriStorico = () => {
      paginaStorico.value = 1;
      storico.value = [];
      totaleStorico.value = 0;
      azzeraIgnorati();
      schedaStorico.value = SCHEDA_PER_FILE;
      mostraStorico.value = true;
      caricaStorico();
    };

    const onPaginaStorico = (e) => {
      paginaStorico.value = e.page + 1;
      caricaStorico();
    };

    const tooltipApri = (imp) => (imp.stato === "CONFERMATO" ? "Apri l'esito" : "Apri l'anteprima");

    // ── Schede dello storico ──────────────────────────────────────────

    const schedaStorico = ref(SCHEDA_PER_FILE);

    const sottotitoloStorico = computed(() => SOTTOTITOLI_STORICO[schedaStorico.value]);

    /**
     * Rilegge la scheda indicata, o quella mostrata, alla pagina e coi filtri
     * correnti. Per gli ignorati rilegge anche l'elenco degli import del filtro.
     */
    const caricaSchedaStorico = (scheda = schedaStorico.value) => {
      if (scheda === SCHEDA_IGNORATI) {
        caricaImportIgnorati();
        return caricaIgnorati();
      }
      return caricaStorico();
    };

    /** Ogni scheda si rilegge quando viene mostrata: l'altra può essere cambiata nel frattempo. */
    const onSchedaStorico = (e) => {
      clearTimeout(timerRicerca); // la rilettura qui sotto vale già per il testo digitato
      caricaSchedaStorico(e.index);
    };

    const caricandoSchedaStorico = computed(() =>
      (schedaStorico.value === SCHEDA_IGNORATI ? caricandoIgnorati.value : caricandoStorico.value));

    // ── Ignorati ──────────────────────────────────────────────────────

    const caricandoIgnorati = ref(false);
    const ignorati = ref([]);
    const totaleIgnorati = ref(0);
    const paginaIgnorati = ref(1);
    const filtriIgnorati = ref(nuoviFiltriIgnorati());
    // null finché la scheda non è stata letta: il riepilogo compare con i dati.
    const conteggiIgnorati = ref(null);

    // Con i cambi di filtro in fila (e il testo digitato) le risposte possono
    // arrivare fuori ordine: vale solo l'ultima richiesta.
    let richiestaIgnorati = 0;
    let timerRicerca = null;

    const caricaIgnorati = async () => {
      const richiesta = ++richiestaIgnorati;
      caricandoIgnorati.value = true;
      try {
        const r = await service.getIgnoratiInsoluti(filtriIgnorati.value, paginaIgnorati.value, IGNORATI_PER_PAGINA);
        if (richiesta !== richiestaIgnorati) return;
        ignorati.value = r.items;
        totaleIgnorati.value = r.total;
        conteggiIgnorati.value = r.conteggi;
      } catch (err) {
        if (richiesta === richiestaIgnorati) gestisciErrore(err, "Righe ignorate non disponibili");
      } finally {
        if (richiesta === richiestaIgnorati) caricandoIgnorati.value = false;
      }
    };

    const azzeraIgnorati = () => {
      richiestaIgnorati += 1; // una risposta ancora in viaggio non riempie la scheda azzerata
      richiestaImport += 1;
      clearTimeout(timerRicerca);
      ignorati.value = [];
      totaleIgnorati.value = 0;
      paginaIgnorati.value = 1;
      filtriIgnorati.value = nuoviFiltriIgnorati();
      conteggiIgnorati.value = null;
      caricandoIgnorati.value = false;
      importStorico.value = [];
      caricandoImport.value = false;
    };

    const onPaginaIgnorati = (e) => {
      paginaIgnorati.value = e.page + 1;
      caricaIgnorati();
    };

    // Gli import fra cui scegliere nel filtro: gli ultimi dello storico, letti
    // quando si mostra la scheda.
    const importStorico = ref([]);
    const caricandoImport = ref(false);
    let richiestaImport = 0;

    const caricaImportIgnorati = async () => {
      const richiesta = ++richiestaImport;
      caricandoImport.value = true;
      try {
        const r = await service.getStoricoInsoluti(1, IMPORT_PER_FILTRO);
        if (richiesta === richiestaImport) importStorico.value = r.items;
      } catch (err) {
        if (richiesta === richiestaImport) gestisciErrore(err, "Elenco degli import non disponibile");
      } finally {
        if (richiesta === richiestaImport) caricandoImport.value = false;
      }
    };

    // ── Filtri degli ignorati ─────────────────────────────────────────

    const filtriIgnoratiAttivi = computed(() => {
      const f = filtriIgnorati.value;
      return f.esiti.length > 0 || f.importId !== null || !!f.dal || !!f.al || f.cerca.trim() !== "";
    });

    /** Ogni cambio di filtro riparte dalla prima pagina; una ricerca in attesa è già compresa. */
    const ripartiIgnorati = () => {
      clearTimeout(timerRicerca);
      paginaIgnorati.value = 1;
      caricaIgnorati();
    };

    const impostaFiltroIgnorati = (chiave, valore) => {
      filtriIgnorati.value[chiave] = valore === undefined ? null : valore;
      ripartiIgnorati();
    };

    const onEsitiIgnorati = (esiti) => {
      filtriIgnorati.value.esiti = esiti || [];
      ripartiIgnorati();
    };

    /** Il testo si rilegge dopo una breve pausa, non a ogni tasto. */
    const onCercaIgnorati = (testo) => {
      filtriIgnorati.value.cerca = testo || "";
      clearTimeout(timerRicerca);
      timerRicerca = setTimeout(ripartiIgnorati, ATTESA_RICERCA_MS);
    };

    const azzeraFiltriIgnorati = () => {
      filtriIgnorati.value = nuoviFiltriIgnorati();
      ripartiIgnorati();
    };

    // ── Riepilogo e vuoto ─────────────────────────────────────────────

    /**
     * Conteggi per esito, solo informativi: gli esiti con almeno una riga,
     * nell'ordine del server. Seguono import, date e testo, non gli esiti.
     */
    const conteggiVisibiliIgnorati = computed(() => {
      const c = conteggiIgnorati.value || {};
      return Object.keys(c)
        .filter((esito) => c[esito] > 0)
        .map((esito) => ({ esito, etichetta: etichettaEsito(esito), numero: c[esito] }));
    });

    /** Il server dà in `conteggi` tutti gli esiti ignorati, anche quelli a zero, nel suo ordine. */
    const opzioniEsitiIgnorati = computed(() => Object.keys(conteggiIgnorati.value || {})
      .map((esito) => ({ esito, etichetta: etichettaEsito(esito) })));

    const totaleConteggiIgnorati = computed(() => {
      const c = conteggiIgnorati.value || {};
      return Object.keys(c).reduce((somma, esito) => somma + (c[esito] || 0), 0);
    });

    const vuotoIgnorati = computed(() => (filtriIgnoratiAttivi.value
      ? {
        titolo: "Nessuna riga ignorata con questi filtri",
        testo: "Allarga i filtri, oppure azzerali per vedere tutte le righe ignorate.",
      }
      : {
        titolo: "Nessuna riga ignorata",
        testo: "Le righe dei PDF che l'import non applica compaiono qui, anche quelle delle anteprime non confermate.",
      }));

    /** Data dell'import della riga e, se non è confermato, il suo stato. */
    const quandoImport = (imp) => {
      const quando = formatDateTime(imp.created_at);
      return imp.stato === "CONFERMATO" ? quando : `${quando} · ${etichettaStatoImport(imp.stato).toLowerCase()}`;
    };

    /** Gli import del filtro: nome del file e data, come nella colonna Import. */
    const opzioniImportIgnorati = computed(() => importStorico.value.map((imp) => ({
      id: imp.id,
      etichetta: `${imp.nome_file} · ${quandoImport(imp)}`,
    })));

    /** Storni citati da una riga ignorata: i candidati (solo id) o lo storno con l'IBAN diverso. */
    const storniCitati = (riga) => {
      if (riga.candidati && riga.candidati.length) return riga.candidati;
      return riga.payment_id ? [riga.payment_id] : [];
    };

    // ── Errori ────────────────────────────────────────────────────────

    const statoHttp = (err) => (err && err.response ? err.response.status : null);

    const messaggioErrore = (err) => {
      return (err && err.response && err.response.data && err.response.data.message)
        || (err && err.message)
        || "Errore imprevisto";
    };

    /** Come nella pagina: il 409 è una collisione prevista, non un guasto. */
    const gestisciErrore = (err, riepilogoErrore) => {
      const stato = statoHttp(err);
      proxy.$toast.add({
        severity: stato === 409 || stato === 403 ? "warn" : "error",
        summary: stato === 409 ? "Operazione non possibile" : riepilogoErrore,
        detail: messaggioErrore(err),
        life: 7000,
      });
    };

    // ── Formattazione ─────────────────────────────────────────────────

    const formatCurrency = (v) => {
      if (v === null || v === undefined) return "—";
      return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
    };

    const formatDateTime = (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "—");

    const formatDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");

    const formatDimensione = (byte) => {
      if (!byte) return "0 KB";
      if (byte < 1024 * 1024) return `${Math.max(1, Math.round(byte / 1024))} KB`;
      return `${(byte / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
    };

    const plurale = (n, uno, molti) => `${n} ${n === 1 ? uno : molti}`;

    const etichettaEsito = (e) => ETICHETTE_ESITO[e] || e || "—";

    const etichettaStatoImport = (s) => ETICHETTE_STATO_IMPORT[s] || s || "—";

    const motivo = (riga) => {
      if (riga.esito === "ambiguo") {
        // Un solo candidato: lo storno è conteso da due righe del PDF (§2.1).
        return riga.candidati && riga.candidati.length === 1
          ? "Lo stesso storno corrisponde a un'altra riga del PDF:"
          : "Più storni possibili:";
      }
      if (riga.esito === "conto_sconosciuto" && riga.creditore_iban) {
        return `${MOTIVI_ESITO.conto_sconosciuto}: ${riga.creditore_iban}`;
      }
      return MOTIVI_ESITO[riga.esito] || "—";
    };

    onBeforeUnmount(() => {
      clearInterval(timerConferma);
      clearTimeout(timerRicerca);
    });

    return {
      mostraImport, fileScelto, erroreCaricamento, caricando, confermando, rileggendo, occupato,
      riepilogo, fase, titoloImport, importCorrente, righeImport, daAggiornare, conteggiVisibili,
      filtro, opzioniFiltro, righeFiltrate, primaRiga, righePerPagina: RIGHE_PER_PAGINA,
      avvisi, iconaAvviso, statoIncerto,
      apriCaricamento, apriImport, onChiusuraImport, onFileScelto, carica,
      mostraConferma, attesaConferma, countdown, progressoConferma, etichettaConferma, messaggioConferma,
      chiediConferma, chiudiConferma, confermaImport, rileggiImport,
      scaricando, scaricaPdf,
      mostraStorico, caricandoStorico, storico, totaleStorico, paginaStorico, perPaginaStorico: STORICO_PER_PAGINA,
      caricaStorico, apriStorico, onPaginaStorico, tooltipApri,
      schedaStorico, sottotitoloStorico, caricaSchedaStorico, onSchedaStorico, caricandoSchedaStorico,
      caricandoIgnorati, ignorati, totaleIgnorati, paginaIgnorati, perPaginaIgnorati: IGNORATI_PER_PAGINA,
      conteggiIgnorati, conteggiVisibiliIgnorati, totaleConteggiIgnorati, vuotoIgnorati,
      onPaginaIgnorati, quandoImport, storniCitati,
      filtriIgnorati, filtriIgnoratiAttivi, opzioniEsitiIgnorati, opzioniImportIgnorati,
      caricandoImport, impostaFiltroIgnorati, onEsitiIgnorati, onCercaIgnorati, ripartiIgnorati, azzeraFiltriIgnorati,
      formatCurrency, formatDateTime, formatDate, formatDimensione, etichettaEsito, etichettaStatoImport, motivo, plurale,
    };
  },
};
</script>

<style scoped src="./rid-sdd-banche-comune.css"></style>

<style scoped>
/* ──────────────────────────────────────────────────────────────────────
   Le classi rb-* comuni con RidSddBanche.vue stanno in
   rid-sdd-banche-comune.css; qui restano le loro varianti e le rb-ins-*,
   proprie dell'import degli insoluti.
   ────────────────────────────────────────────────────────────────────── */

/* ── Dialoghi ──────────────────────────────────────────────────────── */

.rb-vh-sotto {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* Schede dello storico: le tabelle restano allineate al contenuto della finestra */
.rb-ins-schede :deep(.p-tabview-nav li .p-tabview-nav-link) {
  padding: 0.75rem 1rem;
  font-size: 0.88rem;
}

.rb-ins-schede :deep(.p-tabview-panels) { padding: 1rem 0 0; }

/* Conferma con attesa */

.rb-conferma-icona--conferma { background: rgba(16, 185, 129, 0.14); color: #047857; }

/* ── Esito e riepilogo ─────────────────────────────────────────────── */

.rb-esito--warn {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.32);
  color: #b45309;
}

.rb-conf-voce {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
}

.rb-conf-testo {
  font-size: 0.88rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}

/* Conteggi per esito */
.rb-ins-conteggi {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-bottom: 1.15rem;
}

.rb-ins-conteggio-num {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Scheda Ignorati: barra dei filtri sopra la tabella, va a capo quando manca lo spazio */
.rb-ins-filtri {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.85rem 0.9rem;
  padding-top: 0.75rem;
  margin-bottom: 1rem;
}

.rb-ins-f {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  flex: 1 1 12rem;
  min-width: 0;
}

.rb-ins-f--import { flex: 2 1 16rem; }
.rb-ins-f--scadenza { flex: 2 1 17rem; }
.rb-ins-f--cerca { flex: 2 1 14rem; }

.rb-ins-f-lab {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-color-secondary);
}

.rb-ins-f-campo { width: 100%; min-width: 0; }

/* Niente display qui: il tema centra la lente con il flex di .p-input-icon-left */
.rb-ins-f-cerca-box { width: 100%; }

.rb-ins-periodo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rb-ins-periodo-data {
  flex: 1 1 0;
  min-width: 0;
}

.rb-ins-periodo-data :deep(.p-inputtext) {
  width: 100%;
  min-width: 0;
}

.rb-ins-periodo-sep {
  color: var(--text-color-secondary);
  flex-shrink: 0;
}

.rb-ins-azzera { flex: 0 0 auto; }

.rb-ins-nota-filtri {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: -0.35rem 0 0.9rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* Riepilogo: il totale e un badge per esito, senza clic */
.rb-ins-riepilogo {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.6rem;
  margin-bottom: 0.9rem;
}

/* Barra sopra la tabella: conteggio e filtro */

.rb-ins-filtro {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  background: transparent;
}

.rb-ins-filtro :deep(.p-button),
.rb-ins-filtro :deep(.p-button:first-child),
.rb-ins-filtro :deep(.p-button:last-child) {
  border-radius: 20px !important;
  padding: 0.4rem 0.95rem;
  font-size: 0.82rem;
  font-weight: 500;
  border: 1px solid var(--surface-border) !important;
  background: var(--surface-card);
  color: var(--text-color);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.rb-ins-filtro :deep(.p-button:not(.p-highlight):hover) {
  background: var(--surface-hover);
  border-color: var(--primary-color) !important;
  color: var(--primary-color);
}

.rb-ins-filtro :deep(.p-button.p-highlight) {
  background: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* ── Scelta del file ───────────────────────────────────────────────── */

.rb-ins-scelta {
  position: relative;
  padding-top: 0.25rem;
}

/* L'input resta raggiungibile da tastiera: si vede la zona che lo descrive */
.rb-ins-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.rb-ins-zona {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  padding: 2.25rem 1.5rem;
  border: 2px dashed var(--surface-border);
  border-radius: 10px;
  background: var(--surface-ground);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.rb-ins-zona:hover,
.rb-ins-input:focus + .rb-ins-zona {
  border-color: var(--primary-color);
}

.rb-ins-zona i {
  font-size: 2rem;
  color: var(--primary-color);
}

.rb-ins-zona--scelto {
  border-style: solid;
  background: var(--surface-card);
}

.rb-ins-zona--occupata {
  opacity: 0.6;
  cursor: progress;
  pointer-events: none;
}

.rb-ins-zona-nome {
  font-weight: 600;
  font-size: 0.95rem;
  overflow-wrap: anywhere;
}

.rb-ins-zona-meta {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.rb-ins-nota {
  margin: 0.9rem 0;
  font-size: 0.82rem;
  color: var(--text-color-secondary);
}

/* ── Tabelle ───────────────────────────────────────────────────────── */

.rb-ins-email {
  font-size: 0.85rem;
  overflow-wrap: anywhere;
}

/* Badge di stato: esiti di riga e stato dell'import */

.rb-stato--aggiornabile { background: rgba(59, 130, 246, 0.14); color: #1d4ed8; }
.rb-stato--aggiornato,
.rb-stato--confermato { background: rgba(16, 185, 129, 0.14); color: #047857; }
.rb-stato--ambiguo,
.rb-stato--iban_diverso,
.rb-stato--anteprima { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.rb-stato--gia_motivato,
.rb-stato--gia_motivato_nel_frattempo { background: rgba(100, 116, 139, 0.14); color: #475569; }
.rb-stato--non_trovato,
.rb-stato--conto_sconosciuto,
.rb-stato--illeggibile { background: rgba(239, 68, 68, 0.13); color: #b91c1c; }

/* Causale, motivo e candidati */
.rb-ins-causale {
  font-size: 0.82rem;
  line-height: 1.35;
}

.rb-ins-codice {
  display: inline-block;
  margin-right: 0.35rem;
  padding: 0.1rem 0.4rem;
  border-radius: 5px;
  border: 1px solid var(--surface-border);
  background: var(--surface-ground);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.74rem;
  font-weight: 600;
}

.rb-ins-motivo {
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* La motivazione si legge al passaggio del cursore: va fatto capire */
.rb-ins-motivo--motivazione {
  cursor: help;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}

.rb-ins-candidati {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.3rem;
}

.rb-ins-candidato {
  padding: 0.1rem 0.45rem;
  border-radius: 5px;
  background: rgba(245, 158, 11, 0.12);
  color: #b45309;
  font-size: 0.75rem;
  cursor: help;
}

/* Vuoto */
.rb-vuoto {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2.5rem 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

/* ══════════════════════════════════════════════════════════════════════
   Tablet e telefono. La larghezza delle finestre la decide :breakpoints.
   ══════════════════════════════════════════════════════════════════════ */

@media screen and (max-width: 600px) {
  

  
  .rb-ins-filtro {
    width: 100%;
  }

  .rb-ins-azzera {
    flex: 1 1 100%;
    justify-content: center;
  }
}
</style>
